import React, { useState } from 'react';
import { observer } from 'mobx-react';
import type OrderItem from '@this/domain/order_item2';
import { Text as DangerText } from '@this/shared/ui/data_displays/typography';
import { getSmartTicketInfoForOrderItem } from '@this/components/arrangement/virtual_counter/smart_ticket_info';
import { Flex } from '@this/shared/ui/layout/flex';
import { JsonViewer } from '@this/components/arrangement/virtual_counter/virtual_counter_aside/json_viewer';
import { Button } from '@this/shared/ui/inputs/button';
import type TicketOprReservation from '@this/domain/ticket_opr_reservation2';
import { Fetcher, HTTPError } from '@this/src/util';
import type { TicketOprResponseArgs } from '@this/domain/ticket_opr_response';

type Props = {
  orderItem: OrderItem;
  showTicketOprTicketing: boolean;
  ticketOprResponse: TicketOprResponseArgs[];
  onReloadTrip: () => void;
};

interface ReservationState {
  inquiry: {
    loading: boolean;
    time?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    message?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reservation?: any;
    status?: 'success' | 'error';
  };
  ticketing: {
    loading: boolean;
    time?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    message?: string;
    status?: 'success' | 'error';
  };
  downloadTicket: {
    loading: boolean;
    time?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    message?: string;
    status?: 'success' | 'error';
  };
}

interface Reservations {
  [key: number]: ReservationState;
}

export const OrderItemTransportInfo = observer(
  ({ orderItem, showTicketOprTicketing, onReloadTrip, ticketOprResponse }: Props) => {
    const ticketInfo = React.useMemo(
      () =>
        getSmartTicketInfoForOrderItem(
          { transports: orderItem.transports, travelerInformations: orderItem.travelerInformations },
          ticketOprResponse
        ),
      [orderItem, ticketOprResponse]
    );
    const initialReservations: Reservations = {};
    const [results, setResults] = useState<Reservations>(initialReservations);

    const handleTicketOprTicketing = (ticketOprReservation: TicketOprReservation) => {
      const params = {
        ticket_opr_reservation: ticketOprReservation,
        ticket_opr_rsv_id: ticketOprReservation?.ticketOprRsvId
      };

      setResults(prev => {
        const currentReservation = prev[ticketOprReservation.id] || {};
        const currentInquiry = currentReservation.inquiry || {};
        const currentTicketing = currentReservation.ticketing || {};
        const currentDownloadTicket = currentReservation.downloadTicket || {};

        return {
          ...prev,
          [ticketOprReservation.id]: {
            ...currentReservation,
            inquiry: { ...currentInquiry },
            ticketing: { ...currentTicketing, loading: true },
            downloadTicket: { ...currentDownloadTicket }
          }
        };
      });

      Fetcher.post('/arrangement/ticket_opr_ticketings/ticketing', params)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res.data.code === '200') {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentInquiry = currentReservation.inquiry || {};
              const currentDownloadTicket = currentReservation.downloadTicket || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  inquiry: { ...currentInquiry },
                  downloadTicket: { ...currentDownloadTicket },
                  ticketing: {
                    time: res.time,
                    data: res.data,
                    message: `${res.time} 発券依頼が成功しました。結果をお待ちください。`,
                    status: 'success',
                    loading: false
                  }
                }
              };
            });
            onReloadTrip();
          } else {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentInquiry = currentReservation.inquiry || {};
              const currentDownloadTicket = currentReservation.downloadTicket || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  inquiry: { ...currentInquiry },
                  downloadTicket: { ...currentDownloadTicket },
                  ticketing: {
                    time: res.time,
                    message: `${res.time} 発券依頼が失敗しました。ticketoprを確認ください。`,
                    data: { code: res.data.code, error: res.data.body?.error },
                    status: 'error',
                    loading: false
                  }
                }
              };
            });
          }
        })
        .catch(e => {
          const errorMessage =
            e instanceof HTTPError && e.response?.data.error?.message
              ? e.response.data.message
              : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
          const errorTime = e instanceof HTTPError && e.response?.data.error?.time ? e.response.data.message : '';

          setResults(prev => {
            const currentReservation = prev[ticketOprReservation.id] || {};
            const currentInquiry = currentReservation.inquiry || {};
            const currentDownloadTicket = currentReservation.downloadTicket || {};

            return {
              ...prev,
              [ticketOprReservation.id]: {
                ...currentReservation,
                inquiry: { ...currentInquiry },
                downloadTicket: { ...currentDownloadTicket },
                ticketing: {
                  time: errorTime,
                  message: `${errorTime} 発券依頼が失敗しました。ticketoprを確認ください。`,
                  data: {
                    code: e instanceof HTTPError && e.response?.status ? e.response.status : '',
                    error: errorMessage
                  },
                  status: 'error',
                  loading: false
                }
              }
            };
          });
        });
    };

    const handleTicketOprInquiry = (ticketOprReservation: TicketOprReservation) => {
      const params = {
        ticket_opr_reservation: ticketOprReservation,
        ticket_opr_rsv_id: ticketOprReservation?.ticketOprRsvId
      };
      setResults(prev => {
        const currentReservation = prev[ticketOprReservation.id] || {};
        const currentInquiry = currentReservation.inquiry || {};
        const currentTicketing = currentReservation.ticketing || {};
        const currentDownloadTicket = currentReservation.downloadTicket || {};

        return {
          ...prev,
          [ticketOprReservation.id]: {
            ...currentReservation,
            inquiry: { ...currentInquiry, loading: true },
            ticketing: { ...currentTicketing },
            downloadTicket: { ...currentDownloadTicket }
          }
        };
      });

      Fetcher.post('/arrangement/ticket_opr_ticketings/inquiry', params)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res.data.code === '200') {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentTicketing = currentReservation.ticketing || {};
              const currentDownloadTicket = currentReservation.downloadTicket || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  ticketing: { ...currentTicketing },
                  downloadTicket: { ...currentDownloadTicket },
                  inquiry: {
                    time: res.time,
                    data: res.data,
                    message: `${res.time} ID照会が成功しました。`,
                    reservation: res.data.body?.reservation,
                    status: 'success',
                    loading: false
                  }
                }
              };
            });
          } else {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentTicketing = currentReservation.ticketing || {};
              const currentDownloadTicket = currentReservation.downloadTicket || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  ticketing: { ...currentTicketing },
                  downloadTicket: { ...currentDownloadTicket },
                  inquiry: {
                    time: res.time,
                    message: `${res.time} ID照会が失敗しました。ticketoprを確認ください。`,
                    data: { code: res.data.code, error: res.data.body?.error },
                    status: 'error',
                    loading: false
                  }
                }
              };
            });
          }
        })
        .catch(e => {
          const errorMessage =
            e instanceof HTTPError && e.response?.data.error?.message
              ? e.response.data.message
              : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
          const errorTime = e instanceof HTTPError && e.response?.data.error?.time ? e.response.data.message : '';

          setResults(prev => {
            const currentReservation = prev[ticketOprReservation.id] || {};
            const currentTicketing = currentReservation.ticketing || {};
            const currentDownloadTicket = currentReservation.downloadTicket || {};

            return {
              ...prev,
              [ticketOprReservation.id]: {
                ...currentReservation,
                ticketing: { ...currentTicketing },
                downloadTicket: { ...currentDownloadTicket },
                inquiry: {
                  time: errorTime,
                  message: `${errorTime} ID照会が失敗しました。ticketoprを確認ください。`,
                  data: {
                    code: e instanceof HTTPError && e.response?.status ? e.response.status : '',
                    error: errorMessage
                  },
                  status: 'error',
                  loading: false
                }
              }
            };
          });
        });
    };

    const handleTicketOprDownloadTicket = (ticketOprReservation: TicketOprReservation) => {
      const params = {
        ticket_opr_reservation: ticketOprReservation,
        ticket_opr_rsv_id: ticketOprReservation?.ticketOprRsvId
      };
      setResults(prev => {
        const currentReservation = prev[ticketOprReservation.id] || {};
        const currentInquiry = currentReservation.inquiry || {};
        const currentTicketing = currentReservation.ticketing || {};
        const currentDownloadTicket = currentReservation.downloadTicket || {};

        return {
          ...prev,
          [ticketOprReservation.id]: {
            ...currentReservation,
            inquiry: { ...currentInquiry },
            ticketing: { ...currentTicketing },
            downloadTicket: { ...currentDownloadTicket, loading: true }
          }
        };
      });

      Fetcher.post('/arrangement/ticket_opr_ticketings/download_ticket', params)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res.data.code === '200') {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentTicketing = currentReservation.ticketing || {};
              const currentInquiry = currentReservation.inquiry || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  ticketing: { ...currentTicketing },
                  inquiry: { ...currentInquiry },
                  downloadTicket: {
                    time: res.time,
                    data: res.data,
                    message: `${res.time} e-ticketの取得が成功しました。`,
                    status: 'success',
                    loading: false
                  }
                }
              };
            });
          } else {
            setResults(prev => {
              const currentReservation = prev[ticketOprReservation.id] || {};
              const currentTicketing = currentReservation.ticketing || {};
              const currentInquiry = currentReservation.inquiry || {};

              return {
                ...prev,
                [ticketOprReservation.id]: {
                  ...currentReservation,
                  ticketing: { ...currentTicketing },
                  inquiry: { ...currentInquiry },
                  downloadTicket: {
                    time: res.time,
                    message: `${res.time} e-ticketの取得が失敗しました。ticketoprを確認ください。`,
                    data: { code: res.data.code, error: res.data.body?.error },
                    status: 'error',
                    loading: false
                  }
                }
              };
            });
          }
        })
        .catch(e => {
          const errorMessage =
            e instanceof HTTPError && e.response?.data.error?.message
              ? e.response.data.message
              : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
          const errorTime = e instanceof HTTPError && e.response?.data.error?.time ? e.response.data.message : '';

          setResults(prev => {
            const currentReservation = prev[ticketOprReservation.id] || {};
            const currentTicketing = currentReservation.ticketing || {};
            const currentInquiry = currentReservation.inquiry || {};

            return {
              ...prev,
              [ticketOprReservation.id]: {
                ...currentReservation,
                ticketing: { ...currentTicketing },
                inquiry: { ...currentInquiry },
                downloadTicket: {
                  time: errorTime,
                  message: `${errorTime} e-ticketの取得が失敗しました。ticketoprを確認ください。`,
                  data: {
                    code: e instanceof HTTPError && e.response?.status ? e.response.status : '',
                    error: errorMessage
                  },
                  status: 'error',
                  loading: false
                }
              }
            };
          });
        });
    };

    if (ticketInfo.travelers.length === 0) {
      return null;
    }

    return (
      <div>
        <h3 style={{ marginBottom: '4px' }}>TicketOpr予約</h3>
        <Flex flexDirection="column" gap="4px">
          {ticketInfo.travelers.map(t => (
            <div key={t.name}>
              <p>出張者: {t.name}</p>
              <ul style={{ marginLeft: '8px' }}>
                {t.transports.map(transport => (
                  <React.Fragment key={transport.flight}>
                    <li style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <Flex gap="4px">
                        <p>予約便: {transport.flight}</p>
                        <p>{transport.status === '予約済み' ? '成功しました' : transport.status}</p>
                      </Flex>
                      {transport.info && (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {transport.info.split('<br>').map(line => (
                            <p key={line}>{line}</p>
                          ))}
                        </div>
                      )}
                      {transport.retryMessage && (
                        <div
                          style={{
                            whiteSpace: 'pre-wrap',
                            color: transport.status === '予約済み' ? undefined : 'var(--color-primary)'
                          }}
                        >
                          {transport.retryMessage}
                          {transport.status === '予約済み' && <p>リトライに成功しました。</p>}
                        </div>
                      )}
                      {transport.errorMessage && (
                        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--color-danger)' }}>
                          エラーメッセージ: {transport.errorMessage}
                        </div>
                      )}
                    </li>

                    {showTicketOprTicketing && transport.ticketOprReservation && (
                      <>
                        <div>
                          <Button
                            style={{ padding: '5px', marginLeft: '5px', minWidth: '60px' }}
                            loading={results[transport.ticketOprReservation.id]?.inquiry?.loading}
                            onClick={() =>
                              transport.ticketOprReservation &&
                              handleTicketOprInquiry(transport.ticketOprReservation)
                            }
                          >
                            照会する
                          </Button>
                          {results[transport.ticketOprReservation.id]?.inquiry && (
                            <>
                              {results[transport.ticketOprReservation.id].inquiry.status === 'success' && (
                                <>
                                  <div style={{ color: 'var(--color-success)' }}>
                                    {results[transport.ticketOprReservation.id].inquiry.message}
                                  </div>
                                  <JsonViewer
                                    json={results[transport.ticketOprReservation.id].inquiry.reservation}
                                    label="ID照会結果"
                                  />
                                </>
                              )}
                              {results[transport.ticketOprReservation.id].inquiry.status === 'error' && (
                                <>
                                  <DangerText color="danger">
                                    {results[transport.ticketOprReservation.id].inquiry.message}
                                  </DangerText>
                                  <JsonViewer
                                    json={results[transport.ticketOprReservation.id].inquiry.data}
                                    label="ID照会結果"
                                  />
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <div style={{ padding: '4px' }} />
                        <div>
                          <Button
                            style={{ padding: '5px', marginLeft: '5px', minWidth: '60px' }}
                            loading={results[transport.ticketOprReservation.id]?.ticketing?.loading}
                            onClick={() =>
                              transport.ticketOprReservation &&
                              handleTicketOprTicketing(transport.ticketOprReservation)
                            }
                            disabled={transport.ticketOprReservation.status !== 'reservation_info_gotten'}
                          >
                            発券する
                          </Button>
                          {results[transport.ticketOprReservation.id]?.ticketing && (
                            <>
                              {results[transport.ticketOprReservation.id].ticketing.status === 'success' && (
                                <div style={{ color: 'var(--color-success)' }}>
                                  {results[transport.ticketOprReservation.id].ticketing.message}
                                </div>
                              )}
                              {results[transport.ticketOprReservation.id].ticketing.status === 'error' && (
                                <>
                                  <DangerText color="danger">
                                    {results[transport.ticketOprReservation.id].ticketing.message}
                                  </DangerText>
                                  <JsonViewer
                                    json={results[transport.ticketOprReservation.id].ticketing.data}
                                    label="発券指示結果"
                                  />
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <div style={{ padding: '4px' }} />
                        {transport.ticketOprReservation.status === 'ticket_issued' && (
                          <div>
                            <Button
                              style={{ padding: '5px', marginLeft: '5px', minWidth: '60px' }}
                              loading={results[transport.ticketOprReservation.id]?.downloadTicket?.loading}
                              onClick={() =>
                                transport.ticketOprReservation &&
                                handleTicketOprDownloadTicket(transport.ticketOprReservation)
                              }
                              disabled={transport.ticketOprReservation.status !== 'ticket_issued'}
                            >
                              e-ticket控えを取得する
                            </Button>
                            {!results[transport.ticketOprReservation.id]?.downloadTicket?.loading &&
                              (results[transport.ticketOprReservation.id]?.downloadTicket?.data?.body?.s3_key ||
                                transport.ticketOprReservation.s3FileKey) && (
                                <span style={{ padding: '10px', fontSize: '14px' }}>
                                  <a
                                    href={`/arrangement/ticket_opr_ticketings/${transport.ticketOprReservation.id}/show_ticket_pdf`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    PDFを表示する
                                  </a>
                                </span>
                              )}
                            {results[transport.ticketOprReservation.id]?.downloadTicket && (
                              <>
                                {results[transport.ticketOprReservation.id].downloadTicket.status ===
                                  'success' && (
                                  <div style={{ color: 'var(--color-success)' }}>
                                    {results[transport.ticketOprReservation.id].downloadTicket.message}
                                  </div>
                                )}
                                {results[transport.ticketOprReservation.id].downloadTicket.status === 'error' && (
                                  <>
                                    <DangerText color="danger">
                                      {results[transport.ticketOprReservation.id].downloadTicket.message}
                                    </DangerText>
                                    <JsonViewer
                                      json={results[transport.ticketOprReservation.id].downloadTicket.data}
                                      label="e-ticketダウンロード結果"
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </ul>
            </div>
          ))}
        </Flex>
      </div>
    );
  }
);
